export type NextStep = {
    title: string;
    description: string;
    linkLabel: string;
    linkHref: string;
};

// A full set is exactly 3 NextSteps
export type SetOfNextSteps = [NextStep, NextStep, NextStep];

function createNextStep(title: string, description: string, linkLabel: string, linkHref: string): NextStep {
    return {
        title,
        description,
        linkLabel,
        linkHref,
    };
}

const runbooks: NextStep = createNextStep("Make operations tasks reliable and repeatable with runbooks", "Use runbooks to reliably perform routine and emergency operations tasks automatically or at the click of a button.", "Runbooks", "Runbooks");
const triggers: NextStep = createNextStep("Automate deployment workflows with triggers", "Use triggers to automatically create, deploy or promote releases based on events that occur in Octopus.", "Triggers", "ProjectTriggers");
const variables: NextStep = createNextStep("Handle environmental differences with variables", "Variables let you manage and apply the correct values during deployments across environments.", "Variables", "ProjectVariables");
const lifecycles: NextStep = createNextStep("Automate environment promotion with lifecycles", "Lifecycles let you control how the releases of your software are promoted between environments.", "Lifecycles", "Lifecycles");
const octopusForGitHubCopilot: NextStep = createNextStep(
    "Stay in the flow with the Octopus Extension for GitHub Copilot",
    "The Octopus Extension for GitHub Copilot lets you query Octopus, manage deployments, and create and deploy releases and runbooks from your IDE.",
    "Octopus for GitHub Copilot",
    "OctopusExtensionGitHubCopilot"
);
const githubActions: NextStep = createNextStep(
    "Automate complex deployment workflows with Octopus actions",
    "Use Octopus actions to push artifacts, create releases, trigger deployments, and use runbooks in your GitHub workflows.",
    "GitHub Actions",
    "OctopusGitHubActions"
);
const openIdConnect: NextStep = createNextStep(
    "Seamless, secure connectivity with GitHub Actions",
    "OpenID Connect allows your GitHub Actions workflows to communicate with Octopus Deploy without sharing GitHub credentials with Octopus.",
    "OpenID Connect",
    "ServiceAccountOidcIdentitiesGitHubActions"
);
const manualIntervention: NextStep = createNextStep(
    "Pause and review key changes with manual intervention",
    "Manual intervention lets you pause deployments at critical points to require human input before continuing.",
    "Manual intervention step",
    "ManualInterventionAndApprovalStep"
);
const tenants: NextStep = createNextStep("One process to deploy to thousands of tenants", "Easily create customer or location-specific deployment pipelines without duplicating project configuration.", "Tenants", "Tenants");
const tenantTypes: NextStep = createNextStep("Deploy to physical locations, like retail stores, hospitals, banks, and more with tenants", "Explore some of the common tenancy types you can model with Octopus.", "Tenant types", "TenantTypes");
const multiTenancy: NextStep = createNextStep("A modern view of multi-tenancy", "Our white paper discusses how to approach multi-tenancy so you can scale quickly, reliably, and securely.", "White paper", "modern-multi-tenancy");
const runbookExamples: NextStep = createNextStep("There are hundreds of ways to use runbooks", "Explore examples of how you can use runbooks to simplify and automate your operational tasks.", "Runbook examples", "RunbookExamples");
const cacRunbooks: NextStep = createNextStep(
    "Store your runbook process as code in your project repository",
    "You can use version control to track changes to your runbook processes alongside changes to your project code.",
    "Config as Code runbooks",
    "CaCRunbooks"
);

export const fallbackNextSteps: SetOfNextSteps = [triggers, variables, runbooks];
export const kubernetesNextSteps: SetOfNextSteps = [variables, triggers, lifecycles];
export const githubNextSteps: SetOfNextSteps = [octopusForGitHubCopilot, githubActions, openIdConnect];
export const environmentsNextSteps: SetOfNextSteps = [variables, lifecycles, manualIntervention];
export const tenantsNextSteps: SetOfNextSteps = [tenants, tenantTypes, multiTenancy];
export const runbookNextSteps: SetOfNextSteps = [runbooks, runbookExamples, cacRunbooks];

export function getNextSteps(trialSource: string): SetOfNextSteps {
    switch (trialSource) {
        case "k8s-usecase-top":
        case "k8s-usecase-demo":
        case "k8s-usecase-bottom":
        case "k8s-landing-top": {
            return kubernetesNextSteps;
        }

        case "github-usecase-top":
        case "github-usecase-bottom": {
            return githubNextSteps;
        }

        case "environment-promo-usecase-top":
        case "environment-promo-usecase-bottom": {
            return environmentsNextSteps;
        }

        case "tenants-usecase": {
            return tenantsNextSteps;
        }

        case "runbooks-usecase-top":
        case "runbooks-usecase-bottom": {
            return runbookNextSteps;
        }

        default: {
            return fallbackNextSteps;
        }
    }
}
